import { useCallback, useState, useEffect, useRef } from 'react'
import APIClient from '../../../apiClient'
import { showAlertModal } from '../../../store/modals'

interface DownloadLink {
  filename: string
  url: string
}

const DOWNLOAD_REQUEST_KEY = 'kate_download_request_timestamp'

const useDownloadMessages = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [downloadLinks, setDownloadLinks] = useState<DownloadLink[]>([])
  const [isQueueStarted, setIsQueueStarted] = useState(false)
  const [showDownloadConfirmation, setShowDownloadConfirmation] = useState(false)
  const [canRequestDownload, setCanRequestDownload] = useState(true)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Check if user can request download (not already requested today)
  useEffect(() => {
    const checkDownloadEligibility = () => {
      const lastRequestTime = localStorage.getItem(DOWNLOAD_REQUEST_KEY)
      if (lastRequestTime) {
        const lastRequest = new Date(parseInt(lastRequestTime))
        const now = new Date()
        const hoursSinceLastRequest = (now.getTime() - lastRequest.getTime()) / (1000 * 60 * 60)
        
        // Allow new request after 24 hours
        if (hoursSinceLastRequest < 24) {
          setCanRequestDownload(false)
          return
        }
      }
      setCanRequestDownload(true)
    }

    checkDownloadEligibility()
  }, [])

  // Cleanup function to abort ongoing requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const startDownloadQueue = useCallback(async () => {
    try {
      // Cancel any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      
      // Create new AbortController for this request
      abortControllerRef.current = new AbortController()
      
      setIsLoading(true)
      await APIClient.downloadQueueStart(abortControllerRef.current.signal)
      setIsQueueStarted(true)
      
      // Store the request timestamp
      localStorage.setItem(DOWNLOAD_REQUEST_KEY, Date.now().toString())
      setCanRequestDownload(false)
      
      // Show desktop-friendly confirmation modal
      setShowDownloadConfirmation(true)
    } catch (error: any) {
      // Don't show error if it was aborted (component unmounted)  
      if (error.name !== 'AbortError') {
        console.error('Error starting download queue:', error)
        showAlertModal('Failed to start download queue. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  const checkDownloadLinks = useCallback(async () => {
    try {
      // Cancel any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      
      // Create new AbortController for this request
      abortControllerRef.current = new AbortController()
      
      setIsLoading(true)
      const response = await APIClient.downloadSavedMessageZips(abortControllerRef.current.signal)
      const linksData = response.data

      if (linksData && Object.keys(linksData).length > 0) {
        const links = Object.entries(linksData).map(([filename, url]) => ({
          filename,
          url: String(url)
        }))
        setDownloadLinks(links)
        setIsQueueStarted(false)
      } else {
        // No error modal needed for desktop, just no visual feedback
      }
    } catch (error: any) {
      // Don't show error if it was aborted (component unmounted)
      if (error.name !== 'AbortError') {
        console.error('Error checking download links:', error)
        showAlertModal('Failed to check download status. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  const downloadFile = useCallback((url: string, filename: string) => {
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [])

  const clearDownloadLinks = useCallback(() => {
    setDownloadLinks([])
    setIsQueueStarted(false)
  }, [])

  const hideDownloadConfirmation = useCallback(() => {
    setShowDownloadConfirmation(false)
  }, [])

  return {
    isLoading,
    downloadLinks,
    isQueueStarted,
    canRequestDownload,
    showDownloadConfirmation,
    startDownloadQueue,
    checkDownloadLinks,
    downloadFile,
    clearDownloadLinks,
    hideDownloadConfirmation
  }
}

export default useDownloadMessages 